import { useCallback, useEffect, useRef } from "react";

export interface UseNfcKeyboardScanOptions {
  /** When false, listeners are detached */
  enabled: boolean;
  /** Called with trimmed card UID / number when scan completes */
  onScan: (cardNumber: string) => void;
  minLength?: number;
  /** Max ms between first and last key of a burst (USB NFC readers type very fast) */
  burstWindowMs?: number;
  /** Idle ms after last key before auto-complete (when reader omits Enter) */
  idleCompleteMs?: number;
}

/**
 * Captures USB NFC reader input (keyboard wedge).
 * Completes on Enter or after a short idle following a fast key burst.
 */
export function useNfcKeyboardScan({
  enabled,
  onScan,
  minLength = 4,
  burstWindowMs = 400,
  idleCompleteMs = 120,
}: UseNfcKeyboardScanOptions) {
  const bufferRef = useRef("");
  const burstStartRef = useRef<number | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onScanRef = useRef(onScan);

  onScanRef.current = onScan;

  const flush = useCallback(() => {
    const value = bufferRef.current.trim();
    bufferRef.current = "";
    burstStartRef.current = null;
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (value.length >= minLength) {
      onScanRef.current(value);
    }
  }, [minLength]);

  const scheduleIdleFlush = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(flush, idleCompleteMs);
  }, [flush, idleCompleteMs]);

  useEffect(() => {
    if (!enabled) {
      bufferRef.current = "";
      burstStartRef.current = null;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      // Focused scan field receives wedge input via native input events
      if (target?.closest("[data-nfc-scan-input]")) return;

      const isEditable =
        tag === "input" ||
        tag === "textarea" ||
        target?.isContentEditable;

      if (e.key === "Enter") {
        if (bufferRef.current.trim().length >= minLength) {
          e.preventDefault();
          flush();
        }
        return;
      }

      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;

      // Let normal typing in other fields work; only capture when not in another input
      // or when our card field is focused (data attribute on dialog root).
      const captureRoot = document.querySelector("[data-nfc-scan-capture='true']");
      const insideCapture = captureRoot?.contains(target ?? null);
      if (isEditable && !insideCapture) return;

      const now = Date.now();
      if (!burstStartRef.current) burstStartRef.current = now;

      bufferRef.current += e.key;

      if (now - burstStartRef.current <= burstWindowMs) {
        scheduleIdleFlush();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [enabled, flush, minLength, burstWindowMs, scheduleIdleFlush]);

  return { flush };
}
